import z from 'zod'
import type { SchemaAttributes } from '@/api/types'
import { useFeatureFlagClient } from '@/composables/featureFlags.js'
import { useAuth } from '@/composables/useAuth.js'

/**
 * Create the Pendo object if it doesn't exist
 */
declare global {
  interface Window {
    pendo: Pendo
  }
}

// Create the Pendo interface
interface Pendo extends PendoMethods {
  _q: Array<unknown>
}

interface PendoMethods {
  initialize?: (...args: unknown[]) => void
  identify?: (...args: unknown[]) => void
  updateOptions?: (...args: unknown[]) => void
  pageLoad?: (...args: unknown[]) => void
  track?: (...args: unknown[]) => void
}

type PendoTrackingData = {
  visitor: PendoVisitorType
  account: PendoAccountType
}

type PendoVisitorType = {
  // for compatibility's sake this string must be `${user_id}@${provet_id}`
  id: string
  is_admin: SchemaAttributes<'User'>['is_admin']
  // an additional flag to understand the data was tracked from the new frontend
  is_pcloud_next: boolean
  department?: SchemaAttributes<'Department'>['name']
  department_country?: SchemaAttributes<'Department'>['country']
  work_role?: SchemaAttributes<'User'>['work_role']
  profile_language?: SchemaAttributes<'User'>['language']
  resolution?: string
}

type PendoAccountType = {
  // represents the current `provet_id`
  id: string
  // the legacy Provet uses the SENTRY_ENVIRONMENT here, see the link below
  // https://gitlab.com/nordhealth/projects/veterinary/provet-cloud/provetcloud/-/merge_requests/8586/diffs?commit_id=064aaee2aa23e19f7bbe070aae3412a32a4bf334&file=6e2ef14ebbed58b3f8db44123eb18522236303e5#6e2ef14ebbed58b3f8db44123eb18522236303e5_0_31
  environment?: string
  // the organization name
  name?: string
}

const options = z.object({
  apiKey: z.string().min(1),
})

type PendoOptions = z.infer<typeof options>

// Define the methods to be added to the Pendo object
const pendoMethods: Array<keyof PendoMethods> = [
  'initialize',
  'identify',
  'updateOptions',
  'pageLoad',
  'track',
]

function usePendoSetup(options: PendoOptions) {
  const { isLoggedIn, user, activeDepartment } = useAuth()

  window.pendo = window.pendo || ({} as Pendo)
  window.pendo._q = window.pendo._q || []

  // Create functions for each method
  pendoMethods.forEach((method) => {
    window.pendo[method] =
      window.pendo[method] ||
      function (...args: unknown[]) {
        window.pendo._q.push([method, ...args])
      }
  })

  const composePendoPayload = (): PendoTrackingData | undefined => {
    const route = useRoute()
    const runtimeConfig = useRuntimeConfig()

    // If there is a logged in user, then we want to initialize Pendo
    // Otherwise, skip Pendo tracking
    if (isLoggedIn.value && user.value.id) {
      /**
       * Legacy Provet implementation:
       * https://gitlab.com/nordhealth/projects/veterinary/provet-cloud/provetcloud/-/merge_requests/8586/diffs?commit_id=064aaee2aa23e19f7bbe070aae3412a32a4bf334&file=6e2ef14ebbed58b3f8db44123eb18522236303e5#6e2ef14ebbed58b3f8db44123eb18522236303e5_0_19
       *
       * In case there is not user, use the current route name instead. This may happen in the future in pages
       * that won't require a logged in user, e.g. "/:provetId()/onlinebooking".
       */
      const visitorId = `${user.value.id || route.name}@${route.params.provetId}`

      return {
        visitor: {
          id: visitorId,
          is_admin: user.value.attributes?.is_admin || false,
          department: activeDepartment.value?.id,
          department_country: activeDepartment.value?.attributes?.country,
          work_role: user.value?.attributes?.work_role,
          profile_language: user.value?.attributes?.language,
          resolution: `${window.screen.width}x${window.screen.height}`,
          is_pcloud_next: true,
        },
        account: {
          id: route.params.provetId,
          environment: runtimeConfig.public.sentry.environment,
          name: activeDepartment.value?.attributes?.name,
        },
      }
    }

    return undefined
  }

  /**
   * Watch for changes in user and department to update the options
   */
  watch([user, isLoggedIn, activeDepartment], () => {
    const payload = composePendoPayload()

    if (window.pendo.updateOptions && payload) {
      window.pendo.updateOptions(payload)
    }
  })

  useHead({
    script: [
      {
        src: `https://cdn.eu.pendo.io/agent/static/${options.apiKey}/pendo.js`,
        async: true,
      },
    ],
  })

  return {
    initialize: () => {
      const initialPayload = composePendoPayload()
      if (window.pendo.initialize && initialPayload) {
        window.pendo.initialize(initialPayload)
      }
    },
  }
}

export default defineNuxtPlugin({
  name: 'pendo',
  dependsOn: ['vue-query', 'launch-darkly'],
  async setup(nuxtApp) {
    const config = useRuntimeConfig()
    const result = options.safeParse(config.public.pendo)

    if (!result.success) {
      return
    }

    const featureFlagClient = useFeatureFlagClient()

    nuxtApp.hook('app:mounted', async () => {
      const isPendoEnabled =
        await featureFlagClient?.getVariation('temporary.pendo')

      if (!isPendoEnabled) {
        return
      }

      const { initialize } = usePendoSetup(result.data)
      initialize()
    })
  },
})
